import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ImgCropperModal from 'components/containers/task/ImgCropperModal'
import Presenter from 'components/presenters/task/modal/TaskResultFormModal'
import CenterModal from 'components/presenters/modal/CenterModal'
import LoadingModal from 'components/presenters/modal/LoadingModal'
import SocialShareModal from 'components/containers/share/SocialShareModal'
import BottomConfirmModal from 'components/presenters/modal/BottomConfirmModal'
import ImageMergeProcessor from 'components/containers/common/ImageMergeProcessor'

import TaskResultTimeStamp from 'components/containers/task/TaskResultTimeStamp'
import server from 'server/serverController2'
import constants from 'utils/constants'
import _uniqueId from 'lodash/uniqueId'
import { compressImage, getBase64FromUrl, base64ToBlob, convertHeicFile } from 'utils/utils.js'
import { UserPermissionActions } from 'store/actionCreators'

export default function TaskResultFormModal({
  visible,
  setVisible,
  taskInfo,
  taskNo,
  defaultTaskTitle,
  onSubmit,
  onClose,
  onError,
  onLoadModalId,
}) {
  const [modalId] = useState(_uniqueId('task-result-form-modal-'))
  const [displayImg, setDisplayImg] = useState(null)
  const [isValid, setIsValid] = useState(false)
  const [cancelFileSelect, setCancelFileSelect] = useState(false)
  //Center Modal State
  const [initCenterModal, setInitCenterModal] = useState(false)
  const [centerModalVisible, setCenterModalVisible] = useState(false)
  const [centerModalDataList, setCenterModalDataList] = useState({})
  const [onModalComplete, setOnModalComplete] = useState(null)
  const [onModalCancel, setOnModalCancel] = useState(null)

  //Upload
  const fileInputRef = useRef(null)
  const [initUploadState, setInitUploadState] = useState(false)
  const [initImgSelect, setInitImgSelect] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [croppedImg, setCroppedImg] = useState(null)
  const [croppedBase64Img, setCroppedBase64Img] = useState(undefined) // Important
  const [currProofImg, setCurrProofImg] = useState(null)
  const [showCropModal, setShowCropModal] = useState(false)
  const [description, setDescription] = useState(null)

  //Edit TaskResult
  const [initImgURL, setInitImgURL] = useState(null)

  // social share modal
  const [showSocialShareModal, setShowSocialShareModal] = useState(false)
  const [socialShareImg, setSocialShareImg] = useState(null)

  //bottom confirm modal
  const [showBottomConfirmModal, setShowBottomConfirmModal] = useState(false)

  const [isImgLoading, setIsImgLoading] = useState(false)
  const [templateFile, setTemplateFile] = useState(undefined) // Important
  const [mergeFileList, setMergeFileList] = useState([])
  const [loadingTxt, setLoadingTxt] = useState(null)

  //Init Modal
  useEffect(() => {
    if (visible !== true) {
      setInitUploadState(true)
    }
  }, [visible])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fileList = []
    //Important : Order matters. First Arr Item gets lowest z-index for image merge.
    if (croppedBase64Img) {
      fileList.push(croppedBase64Img)
    }
    if (templateFile) {
      fileList.push(templateFile)
    }
    setMergeFileList(fileList)
  }, [templateFile, croppedBase64Img])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (cancelFileSelect === true) {
      setCancelFileSelect(false)
      if (initImgSelect === true) {
        setInitUploadState(true)
        if (onClose) {
          onClose()
        }
      }
    }
  }, [cancelFileSelect])

  useEffect(() => {
    if (initUploadState === true) {
      fileInputRef.current.value = null
      setInitImgURL(null)
      setDescription(null)
      setShowCropModal(false)
      setCurrProofImg(null)
      setCroppedImg(null)
      setCroppedBase64Img(undefined)
      setIsSubmitting(false)
      setInitUploadState(false)
      setInitImgSelect(false)
      setCancelFileSelect(false)
      setTemplateFile(undefined)
      setMergeFileList([])
      setIsImgLoading(false)
    }
  }, [initUploadState])

  //Init Center Modal State
  useEffect(() => {
    if (initCenterModal === true) {
      setCenterModalVisible(false)
      setCenterModalDataList({})
      setOnModalComplete(null)
      setOnModalCancel(null)
      setInitCenterModal(false)
    }
  }, [initCenterModal])

  useEffect(() => {
    if (taskNo) {
      //load proof
      server.getRequestData(`tasks/${taskNo}/result`, (res) => {
        if (res.success !== 1) {
          return false
        }
        const jsonData = res.data
        if (jsonData.task) {
          const targetTask = jsonData.task
          setDescription(targetTask['description'])
          if (targetTask?.proof[0]) {
            setInitImgURL(`${constants.imagePath}/task_img/${targetTask['proof'][0]}`)
          }
        }
      })

      //load task Template
      server.getRequestData(`tasks/${taskNo}/templates`, async (res) => {
        try {
          if (res.success !== 1) {
            return false
          }
          const jsonData = res.data
          if (jsonData.templates) {
            const templates = jsonData.templates
            if (templates.length > 0) {
              const templateFileName = templates[0]['file_name']
              const base64Template = await getBase64FromUrl(
                `${constants.imagePath}/task_template_img/${templateFileName}`,
              )
              setTemplateFile(base64Template)
            } else {
              setTemplateFile(null)
            }
          } else {
            setTemplateFile(null)
          }
        } catch (err) {
          console.log(err)
        }
      })
    }
  }, [taskNo])

  //check validation
  useEffect(() => {
    if (displayImg) {
      setIsValid(true)
    }
  }, [displayImg])
  useEffect(() => {
    const setInitDisplayImg = async () => {
      if (croppedImg) {
        let reader = new FileReader()
        reader.onloadend = (event) => {
          event.preventDefault()
          setCroppedBase64Img(reader.result)
        }
        reader.readAsDataURL(croppedImg)
      } else if (initImgURL) {
        const dataUrl = await getBase64FromUrl(initImgURL)
        setDisplayImg(dataUrl)
        setSocialShareImg(dataUrl)
      } else {
        setDisplayImg(null)
        setIsValid(false)
      }
    }
    setInitDisplayImg()
  }, [initImgURL, croppedImg])

  const handleOpenCropModal = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      if (setVisible) {
        setVisible(true)
      }
      let targetImg = e.target.files[0]
      const fileExt = targetImg?.name?.split('.')?.slice(-1)?.pop()
      if (fileExt) {
        if (fileExt.toLowerCase() === 'heic') {
          setIsImgLoading(true)
          targetImg = await convertHeicFile(targetImg)
          setIsImgLoading(false)
        }
      }
      setCurrProofImg(targetImg)
      setShowCropModal(true)
    }
  }

  const cancelImg = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = null
    }
    setInitImgURL(null)
    setCroppedImg(null)
    setCroppedBase64Img(undefined)
    setDisplayImg(null)
    setSocialShareImg(null)
    setCurrProofImg(null)
  }

  const selectImg = () => {
    fileInputRef.current.click()
  }
  const onChangeDescription = (val) => {
    setDescription(val)
  }

  const handleSubmitClick = () => {
    if (taskInfo?.share_req === true) {
      setShowSocialShareModal(true)
      return
    }

    submitResult()
  }

  const submitResult = async () => {
    try {
      setLoadingTxt(null)
      if (!isSubmitting && taskNo) {
        setIsSubmitting(true)
        let formData = new FormData()
        if (croppedImg && initImgURL === null) {
          //original_files
          const finalImg = await base64ToBlob(displayImg)
          let compressedImg = await compressImage(finalImg)
          formData.append('file', compressedImg)

          let compressedOriginalImg = await compressImage(croppedImg)
          formData.append('original_file', compressedOriginalImg)
        }
        if (![null, undefined].includes(description)) {
          formData.append('description', description)
        }

        server.postRequestFormData(
          `tasks/${taskNo}/result`,
          formData,
          async (res) => {
            try {
              if (res?.success !== 1) {
                let errData = res?.error?.res_data

                setCenterModalVisible(true)
                if (errData?.detail_code === 'TASK_DATE_NOT_ALLOWED') {
                  setCenterModalDataList({
                    description: '인증 가능한 시간이 아니에요.',
                  })
                  setOnModalComplete(() => () => {
                    setInitCenterModal(true)
                  })
                } else if (errData?.detail_code === 'SUBMITION_TIME_NOT_STARTED') {
                  setCenterModalDataList({
                    description: '아직 인증시간이 시작되지 않았어요.',
                  })
                  setOnModalComplete(() => () => {
                    setInitCenterModal(true)
                  })
                } else if (errData?.detail_code === 'SUBMITION_TIME_ENDED') {
                  setCenterModalDataList({
                    description: '이미 인증시간이 지났어요.',
                  })
                  setOnModalComplete(() => () => {
                    setInitCenterModal(true)
                  })
                } else {
                  setCenterModalDataList({
                    description: '예기치못한 에러가 발생했어요. 다시 시도해주세요.',
                  })
                  setOnModalComplete(() => () => {
                    setInitCenterModal(true)
                  })
                }
                onClose()
                setIsSubmitting(false)
                return
              }
              let jsonData = res['data']

              const targetItem = jsonData?.updated_item
                ? jsonData['updated_item'][0]
                : jsonData['inserted_item']
              const permissions = jsonData['permissions'] ? jsonData['permissions'] : null
              if (permissions?.length > 0) {
                const permissionTypes = []
                for (let i = 0; i < permissions.length; ++i) {
                  const permissionObj = {}
                  permissionObj[permissions[i]['name']] =
                    permissions[i]['action'] === 'add' ? true : false
                  permissionTypes.push(permissions[i]['name'])
                  await UserPermissionActions.updateUserPermission(permissionObj)
                }
              }
              if (onSubmit) {
                const response = {}
                if (jsonData?.updated_item) {
                  response['type'] = 'updated'
                } else {
                  response['type'] = 'inserted'
                }
                response['data'] = targetItem
                response['permissions'] = permissions
                onSubmit(response)
              }
              setIsSubmitting(false)
            } catch (err) {
              setCenterModalVisible(true)
              setCenterModalDataList({
                description: '인증 사진 업로드를 실패했어요.\n다시 시도해주세요.',
              })
              setOnModalComplete(() => () => {
                setInitCenterModal(true)
              })
              onClose()
              setIsSubmitting(false)
            }
          },
          {
            onUploadProgress: (evt) => {
              setLoadingTxt('업로드중...')
            },
            onDownloadProgress: (evt) => {
              setLoadingTxt('처리중...')
            },
          },
        )
      }
    } catch (err) {
      setCenterModalVisible(true)
      setCenterModalDataList({
        description: '인증 사진 업로드를 실패했어요.\n다시 시도해주세요.',
      })
      setOnModalComplete(() => () => {
        setInitCenterModal(true)
      })
      onClose()
      setIsSubmitting(false)
    }
  }
  useEffect(() => {
    if (onLoadModalId) {
      onLoadModalId(`${modalId}`)
    }
  }, [])

  return (
    <>
      {visible && !initImgSelect ? (
        <Presenter
          modalId={modalId}
          description={description}
          isValid={isValid}
          isImgLoading={isImgLoading}
          displayImg={initImgURL ? initImgURL : displayImg}
          onSubmit={() => {
            // submitResult();
            handleSubmitClick()
          }}
          onClose={() => {
            setCenterModalVisible(true)
            setCenterModalDataList({
              title: '게시물 작성 취소',
              description: '게시물 작성을 취소하시겠어요?',
            })
            setOnModalComplete(() => () => {
              if (onClose) onClose()
              setInitCenterModal(true)
            })
            setOnModalCancel(() => () => {
              setInitCenterModal(true)
            })
          }}
          onSelectImg={() => {
            selectImg()
          }}
          onCancelImg={() => {
            setCenterModalVisible(true)
            setCenterModalDataList({
              title: '사진 삭제',
              description: '사진을 삭제하시겠어요?',
            })
            setOnModalComplete(() => () => {
              if (cancelImg) cancelImg()
              setInitCenterModal(true)
            })
            setOnModalCancel(() => () => {
              setInitCenterModal(true)
            })
          }}
          onChangeDescription={(value) => {
            onChangeDescription(value)
          }}
        />
      ) : null}
      {showCropModal ? (
        <ImgCropperModal
          visible={showCropModal}
          srcImg={currProofImg}
          croppedImg={croppedImg}
          setCroppedImg={setCroppedImg}
          onCancel={(e) => {
            if (fileInputRef?.current) {
              fileInputRef.current.value = null
            }
            setCroppedImg(null)
            setCroppedBase64Img(undefined)
            setDisplayImg(null)
            setSocialShareImg(null)
            setCurrProofImg(null)
            setShowCropModal(false)
            if (initImgSelect === true) {
              setInitUploadState(true)
              setInitImgSelect(false)
              if (onClose) {
                onClose()
              }
            }
          }}
          onComplete={(e) => {
            setInitImgURL(null)
            setShowCropModal(false)
            setInitImgSelect(false)
          }}
          onError={(error) => {
            if (error['detail_code'] === 'IMG_NOT_LOADED') {
              setInitUploadState(true)
              setCenterModalVisible(true)
              setCenterModalDataList({
                description: '이미지 로드를 실패했어요.\n 다시 시도해주세요.',
              })
              setOnModalComplete(() => () => {
                setInitCenterModal(true)
              })
            } else {
              setInitUploadState(true)
              setCenterModalVisible(true)
              setCenterModalDataList({
                description: '이미지 등록을 실패했어요.\n 다시 시도해주세요.',
              })
              setOnModalComplete(() => () => {
                setInitCenterModal(true)
              })
            }
          }}
        />
      ) : null}

      {isSubmitting ? <LoadingModal zIndex={500} loadingTxt={loadingTxt} /> : null}
      <CenterModal
        modalVisible={centerModalVisible}
        setModalVisible={setCenterModalVisible}
        dataList={centerModalDataList}
        onCancel={onModalCancel}
        onComplete={onModalComplete}
      />
      <ImgFileInput
        id={`taskResultImg${modalId}`}
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleOpenCropModal}
      />
      <SocialShareModal
        visible={showSocialShareModal}
        shareImg={socialShareImg}
        taskNo={taskNo}
        onClose={(shareTriggered) => {
          setShowSocialShareModal(false)
        }}
        onShare={() => {
          submitResult()
          setShowBottomConfirmModal(true)
        }}
      />

      <BottomConfirmModal
        visible={showBottomConfirmModal}
        title={'인스타에 공유하셨나요?'}
        description={`인스타에 공유 완료 해야\n챌린지 인증이 완료됩니다!`}
        onCancel={() => {
          setShowBottomConfirmModal(false)
        }}
        onComplete={() => {
          setShowBottomConfirmModal(false)
          setShowSocialShareModal(false)
          //submitResult();
        }}
      />
      {croppedBase64Img !== undefined &&
        templateFile !== undefined &&
        mergeFileList.length === [templateFile, croppedBase64Img].filter((n) => n).length && (
          <ImageMergeProcessor
            base64Images={mergeFileList} //mergeFileList
            domTemplate={[<TaskResultTimeStamp key={'task-result-timestamp'} />]}
            onComplete={(mergedImg) => {
              setDisplayImg(mergedImg)
              setSocialShareImg(mergedImg)
              setIsImgLoading(false)
            }}
            onProcessing={() => {
              //onMerge
              setIsImgLoading(true)
            }}
            onError={() => {
              setInitUploadState(true)
              setCenterModalVisible(true)
              setCenterModalDataList({
                description: '이미지 로드를 실패했어요.\n 다시 시도해주세요.',
              })
              setOnModalComplete(() => () => {
                setInitCenterModal(true)
              })
            }}
          />
        )}
    </>
  )
}
export const ImgFileInput = styled.input`
  display: none;
`
